<template>
  <div>
    <div class="mb-4 flex items-center pl-6">
      <vs-button @click="handleBackClick" color="dark" type="filled" icon-pack="feather" icon="icon-arrow-left">
        Back
      </vs-button>
    </div>

    <form-wizard
      ref="formWizard"
      id="submit-sarai-form"
      color="rgba(var(--vs-primary), 1)"
      errorColor="rgba(var(--vs-danger), 1)"
      :title="wizardTitle"
      subtitle="Sarai Information"
      finishButtonText="Submit"
      @on-change="updateCurrentStep"
      @on-complete="submitForm"
    >
      <!-- Step 1: Basic Information -->
      <tab-content title="Basic Info" class="mb-5" icon="feather icon-home" :before-change="validateStep1">
        <form data-vv-scope="step-1">
          <div class="vx-row">
            <div class="vx-col md:w-1/2 w-full mb-4">
              <p class="mb-2 text-lg font-bold">Gurudwara *</p>
              <select-2
                class="w-full"
                name="gurudwara"
                placeholder="Select Gurudwara"
                :value="formData.gurudwara"
                v-model="formData.gurudwara"
                @input="handleGurudwaraSelect"
                :options="gurudwaraList"
                autocomplete
                :ssr="false"
                :multiple="false"
                :disabled="isEditMode"
              />
              <span v-if="step1Errors.gurudwara" class="text-danger text-sm">{{ step1Errors.gurudwara }}</span>
            </div>

            <div class="vx-col md:w-1/2 w-full mb-4">
              <p class="mb-2 text-lg font-bold">Sarai Name *</p>
              <vs-input v-model="formData.name" placeholder="Enter Sarai Name" class="w-full" />
              <span v-if="step1Errors.name" class="text-danger text-sm">{{ step1Errors.name }}</span>
            </div>

            <div class="vx-col w-full mb-4">
              <p class="mb-2 text-lg font-bold">Walking Distance</p>
              <vs-input v-model="formData.walking_distance" placeholder="e.g., 5 minutes walk from gurudwara" class="w-full" />
            </div>

            <div class="vx-col w-full mb-4">
              <p class="mb-2 text-lg font-bold">Location Link (Google Maps)</p>
              <vs-input v-model="formData.location_link" placeholder="Enter Google Maps link" class="w-full" />
            </div>

            <div class="vx-col w-full mb-4">
              <p class="mb-2 text-lg font-bold">Images</p>
              <input
                :key="fileKey"
                type="file"
                class="rounded w-full text-lg file-upload"
                accept=".jpg, .png, .jpeg"
                @change="handleFileUpload"
                multiple
              />
              <div class="mt-4 flex flex-wrap gap-4" v-if="formData.images && formData.images.length > 0">
                <div v-for="(image, index) in formData.images" :key="index" class="relative" style="width: 150px; height: 150px;">
                  <img :src="image" alt="Preview" style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px;" />
                  <div class="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer" @click="removeImage(index)">×</div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </tab-content>

      <!-- Step 2: Room Management -->
      <tab-content title="Rooms" class="mb-5" icon="feather icon-bed" :before-change="validateStep2">
        <div class="mb-4">
          <div class="flex justify-between items-center mb-4">
            <p class="text-lg font-bold">Room Types</p>
            <vs-button @click="addRoom" color="primary" size="small">
              <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" /> Add Room
            </vs-button>
          </div>

          <div v-for="(room, index) in formData.rooms" :key="index" class="border rounded-lg p-4 mb-4">
            <div class="flex justify-between items-start mb-4">
              <h4 class="font-bold">Room {{ index + 1 }}</h4>
              <vs-button @click="removeRoom(index)" color="danger" size="small" type="flat">
                <feather-icon icon="TrashIcon" svgClasses="h-4 w-4" />
              </vs-button>
            </div>

            <div class="vx-row">
              <div class="vx-col md:w-1/2 w-full mb-4">
                <p class="mb-2 font-bold">Room Type *</p>
                <vs-select v-model="room.room_type" placeholder="Select Room Type" class="w-full">
                  <vs-select-item :key="type.value" :value="type.value" :text="type.label" v-for="type in roomTypes" />
                </vs-select>
                <span v-if="roomErrors[index] && roomErrors[index].room_type" class="text-danger text-sm">{{ roomErrors[index].room_type }}</span>
              </div>

              <div class="vx-col md:w-1/2 w-full mb-4">
                <p class="mb-2 font-bold">Room Name *</p>
                <vs-input v-model="room.name" placeholder="e.g., 1 Bed AC Room" class="w-full" />
                <span v-if="roomErrors[index] && roomErrors[index].name" class="text-danger text-sm">{{ roomErrors[index].name }}</span>
              </div>

              <div class="vx-col md:w-1/2 w-full mb-4">
                <p class="mb-2 font-bold">Bed Type</p>
                <vs-input v-model="room.bed_type" placeholder="e.g., 1 king size bed" class="w-full" />
              </div>

              <div class="vx-col md:w-1/2 w-full mb-4">
                <p class="mb-2 font-bold">Room Size</p>
                <vs-input v-model="room.room_size" placeholder="e.g., 9 m²" class="w-full" />
              </div>

              <div class="vx-col md:w-1/2 w-full mb-4" v-if="room.room_type === 'dormitory_hall'">
                <p class="mb-2 font-bold">Capacity *</p>
                <vs-input type="number" v-model.number="room.capacity" placeholder="e.g., 20" class="w-full" />
              </div>

              <div class="vx-col md:w-1/2 w-full mb-4">
                <p class="mb-2 font-bold">Price Per Night (Rs.) *</p>
                <vs-input type="number" v-model.number="room.price_per_night" placeholder="500" class="w-full" />
                <span v-if="roomErrors[index] && roomErrors[index].price_per_night" class="text-danger text-sm">{{ roomErrors[index].price_per_night }}</span>
              </div>

              <div class="vx-col md:w-1/2 w-full mb-4">
                <p class="mb-2 font-bold">Original Price (Rs.) - Optional</p>
                <vs-input type="number" v-model.number="room.original_price" placeholder="900" class="w-full" />
              </div>

              <div class="vx-col w-full mb-4">
                <p class="mb-2 font-bold">Description</p>
                <vs-textarea v-model="room.description" placeholder="Room description" height="100px" />
              </div>

              <div class="vx-col w-full mb-4">
                <p class="mb-2 font-bold">Room Images</p>
                <input
                  :key="`room-${index}-${roomFileKey[index] || 0}`"
                  type="file"
                  class="rounded w-full text-lg file-upload"
                  accept=".jpg, .png, .jpeg"
                  @change="(e) => handleRoomFileUpload(e, index)"
                  multiple
                />
                <div class="mt-4 flex flex-wrap gap-4" v-if="room.images && room.images.length > 0">
                  <div v-for="(img, imgIndex) in room.images" :key="imgIndex" class="relative" style="width: 120px; height: 120px;">
                    <img :src="img" alt="Preview" style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px;" />
                    <div class="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center cursor-pointer text-xs" @click="removeRoomImage(index, imgIndex)">×</div>
                  </div>
                </div>
              </div>

              <div class="vx-col w-full mb-4">
                <div class="flex items-center gap-4">
                  <vs-checkbox v-model="room.extra_bedding_available">Extra Bedding Available</vs-checkbox>
                  <vs-input
                    v-if="room.extra_bedding_available"
                    type="number"
                    v-model.number="room.extra_bedding_price"
                    placeholder="Extra Bedding Price"
                    class="w-48"
                  />
                </div>
              </div>

              <div class="vx-col w-full mb-4">
                <vs-switch v-model="room.is_active">
                  <span slot="on">Active</span>
                  <span slot="off">Inactive</span>
                </vs-switch>
              </div>
            </div>
          </div>

          <div v-if="formData.rooms.length === 0" class="text-center py-8 text-gray-500">
            No rooms added. Click "Add Room" to add a room.
          </div>
        </div>
      </tab-content>

      <!-- Step 3: Additional Information -->
      <tab-content title="Additional Info" class="mb-5" icon="feather icon-info" :before-change="validateStep3">
        <div class="vx-row">
          <div class="vx-col w-full mb-4">
            <p class="mb-2 text-lg font-bold">Amenities</p>
            <vs-textarea v-model="formData.amenities" placeholder="Enter amenities (placeholder data)" height="150px" />
          </div>

          <div class="vx-col w-full mb-4">
            <p class="mb-2 text-lg font-bold">Sarai Rules</p>
            <vs-textarea v-model="formData.sarai_rules" placeholder="Enter sarai rules (placeholder data)" height="150px" />
          </div>

          <div class="vx-col w-full mb-4">
            <p class="mb-2 text-lg font-bold">Cancellation Policy</p>
            <vs-textarea v-model="formData.cancellation_policy" placeholder="Enter cancellation policy (placeholder data)" height="150px" />
          </div>

          <div class="vx-col w-full mb-4">
            <p class="mb-2 text-lg font-bold">Safety Information</p>
            <vs-textarea v-model="formData.safety_information" placeholder="Enter safety information (placeholder data)" height="150px" />
          </div>

          <div class="vx-col w-full mb-4">
            <div class="flex items-center gap-4">
              <vs-switch v-model="formData.is_active">
                <span slot="on">Active</span>
                <span slot="off">Inactive</span>
              </vs-switch>
              <span class="font-bold">Is Active</span>
            </div>
          </div>

          <div class="vx-col w-full mb-4">
            <div class="flex items-center gap-4">
              <vs-switch v-model="formData.is_verified">
                <span slot="on">Verified</span>
                <span slot="off">Not Verified</span>
              </vs-switch>
              <span class="font-bold">Verified and added by gurudwara</span>
            </div>
          </div>
        </div>
      </tab-content>
    </form-wizard>
  </div>
</template>

<script>
import axios from '@/axios'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'SaraiAction',
  components: {
    FormWizard,
    TabContent,
    'select-2': Select2
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    selectedRecordId: {
      type: String,
      default: null
    },
    selectedRecordData: {
      type: Object,
      default: null
    },
    action_name: {
      type: String,
      default: 'Add'
    }
  },
  data() {
    return {
      currentStep: 0,
      fileKey: 0,
      roomFileKey: {},
      formData: {
        gurudwara: null,
        name: '',
        images: [],
        walking_distance: '',
        location_link: '',
        amenities: '',
        sarai_rules: '',
        cancellation_policy: '',
        safety_information: '',
        rooms: [],
        is_active: true,
        is_verified: false
      },
      gurudwaraList: [],
      roomTypes: [
        { value: '1_bed_ac', label: '1 Bed AC' },
        { value: '1_bed_non_ac', label: '1 Bed Non-AC' },
        { value: '2_bed_ac', label: '2 Bed AC' },
        { value: '2_bed_non_ac', label: '2 Bed Non-AC' },
        { value: 'dormitory_hall', label: 'Dormitory Hall (20 people capacity)' }
      ],
      step1Errors: {},
      step2Errors: {},
      step3Errors: {},
      roomErrors: []
    }
  },
  computed: {
    isEditMode() {
      return !!this.selectedRecordId
    },
    wizardTitle() {
      return this.isEditMode ? 'Edit Sarai' : 'Add Sarai'
    }
  },
  watch: {
    showModal(val) {
      if (val) {
        this.loadData()
      }
    }
  },
  mounted() {
    this.loadGurudwaras()
    if (this.selectedRecordId) {
      this.loadData()
    }
  },
  methods: {
    async loadGurudwaras() {
      try {
        const response = await axios.get('/gurudwara', { params: { limit: 1000 } })
        if (response.data.success) {
          this.gurudwaraList = (response.data.data.results || []).map(g => ({
            value: g._id,
            label: g.name
          }))
        }
      } catch (error) {
        console.error('Failed to load gurudwaras:', error)
      }
    },
    handleGurudwaraSelect(item) {
      this.formData.gurudwara = item && item.value ? item.value : null
    },
    async loadData() {
      if (!this.selectedRecordId) return

      try {
        const response = await axios.get(`/admin/sarai/${this.selectedRecordId}`)
        if (response.data.success) {
          const data = response.data.data
          this.formData = {
            gurudwara: (data.gurudwara && data.gurudwara._id) || null,
            name: data.name || '',
            images: data.images || [],
            walking_distance: data.walking_distance || '',
            location_link: data.location_link || '',
            amenities: data.amenities || '',
            sarai_rules: data.sarai_rules || '',
            cancellation_policy: data.cancellation_policy || '',
            safety_information: data.safety_information || '',
            rooms: data.rooms || [],
            is_active: data.is_active !== undefined ? data.is_active : true,
            is_verified: data.is_verified || false
          }
        }
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load sarai data',
          color: 'danger'
        })
      }
    },
    addRoom() {
      this.formData.rooms.push({
        room_type: '',
        name: '',
        description: '',
        bed_type: '',
        room_size: '',
        capacity: 1,
        price_per_night: null,
        original_price: null,
        images: [],
        extra_bedding_available: false,
        extra_bedding_price: null,
        is_active: true
      })
    },
    removeRoom(index) {
      this.formData.rooms.splice(index, 1)
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      if (!this.formData.images) {
        this.formData.images = []
      }
      files.forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.formData.images.push(e.target.result)
        }
        reader.readAsDataURL(file)
      })
      this.fileKey++
    },
    removeImage(index) {
      this.formData.images.splice(index, 1)
      this.fileKey++
    },
    handleRoomFileUpload(event, roomIndex) {
      const files = Array.from(event.target.files)
      if (!this.formData.rooms[roomIndex].images) {
        this.formData.rooms[roomIndex].images = []
      }
      files.forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.formData.rooms[roomIndex].images.push(e.target.result)
        }
        reader.readAsDataURL(file)
      })
      if (!this.roomFileKey[roomIndex]) {
        this.$set(this.roomFileKey, roomIndex, 0)
      }
      this.roomFileKey[roomIndex]++
    },
    removeRoomImage(roomIndex, imageIndex) {
      this.formData.rooms[roomIndex].images.splice(imageIndex, 1)
      if (!this.roomFileKey[roomIndex]) {
        this.$set(this.roomFileKey, roomIndex, 0)
      }
      this.roomFileKey[roomIndex]++
    },
    validateStep1() {
      this.step1Errors = {}
      let isValid = true

      if (!this.formData.gurudwara) {
        this.step1Errors.gurudwara = 'Gurudwara is required'
        isValid = false
      }

      if (!this.formData.name || this.formData.name.trim() === '') {
        this.step1Errors.name = 'Sarai Name is required'
        isValid = false
      }

      return isValid
    },
    validateStep2() {
      this.roomErrors = []
      let isValid = true

      if (this.formData.rooms.length === 0) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please add at least one room',
          color: 'danger'
        })
        return false
      }

      this.formData.rooms.forEach((room, index) => {
        const errors = {}
        if (!room.room_type) {
          errors.room_type = 'Room type is required'
          isValid = false
        }
        if (!room.name || room.name.trim() === '') {
          errors.name = 'Room name is required'
          isValid = false
        }
        if (!room.price_per_night || room.price_per_night <= 0) {
          errors.price_per_night = 'Price per night is required and must be greater than 0'
          isValid = false
        }
        if (room.room_type === 'dormitory_hall' && (!room.capacity || room.capacity <= 0)) {
          errors.capacity = 'Capacity is required for dormitory hall'
          isValid = false
        }
        this.roomErrors.push(errors)
      })

      return isValid
    },
    validateStep3() {
      // Step 3 doesn't require validation
      return true
    },
    updateCurrentStep(step) {
      this.currentStep = step
    },
    async submitForm() {
      try {
        const payload = { ...this.formData }
        
        if (this.isEditMode) {
          await axios.put(`/admin/sarai/${this.selectedRecordId}`, payload)
        } else {
          await axios.post('/admin/sarai/create', payload)
        }

        this.$vs.notify({
          title: 'Success',
          text: `Sarai ${this.isEditMode ? 'updated' : 'created'} successfully`,
          color: 'success'
        })

        this.$emit('update-data')
        this.handleBackClick()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to save sarai',
          color: 'danger'
        })
      }
    },
    handleBackClick() {
      this.$emit('update:showModal', false)
      if (this.$route.name.includes('sarai')) {
        this.$router.push('/sarai/list')
      }
    }
  }
}
</script>

